import { waitFor } from '@ember/test-waiters';
import { tracked } from '@glimmer/tracking';
import { dropTask, type TaskInstance } from 'ember-concurrency';
import type ActiveStorageService from 'garaje/services/active-storage-extension';
import type { Document, FileUpload } from 'utils/direct-uploader';

function noop(): void {
  /* noop */
}

export class StandaloneFileUpload implements FileUpload {
  /**
   * ActiveStorage service. This needs to be passed in to the constructor because we can't use Ember's
   * `@service` inject as this object doesn't have any "owner".
   */
  activeStorage: ActiveStorageService;
  /**
   * Base URL to use for uploading the file.
   */
  directUploadURL: string;
  /**
   * The file to upload.
   */
  file: File;
  /**
   * Holds a flag for whether the file is valid or not. This is only set/read by calling code;
   * it's not calculated by this class.
   */
  @tracked isValid = false;
  /**
   * Optional prefix to be passed (via `prefix` query param) when file is uploaded.
   */
  prefix?: string;
  /**
   * Upload progress, as reported by the `onProgress` callback of `ActiveStorageService#upload`.
   */
  @tracked progress?: number;
  /**
   * Whether the file has been uploaded or not.
   */
  @tracked uploaded = false;

  declare document?: Document;
  declare readerResult?: string;

  /**
   * `reset` defaults to a noop, but can be overridden if desired.
   */
  reset = noop;

  constructor(
    file: File,
    {
      activeStorage,
      directUploadURL,
      prefix,
    }: { activeStorage: ActiveStorageService; directUploadURL: string; prefix?: string },
  ) {
    this.file = file;
    this.activeStorage = activeStorage;
    this.directUploadURL = directUploadURL;
    this.prefix = prefix;
  }

  upload(): TaskInstance<string | undefined> {
    return this.uploadTask.perform();
  }

  uploadTask = dropTask(
    waitFor(async () => {
      this.uploaded = false;
      this.progress = 0;

      const { directUploadURL, prefix } = this;

      // ember-active-storage addon does not support extra params in the POST payload.
      // But API currently supports sending the prefix as a query param.
      const endpoint = prefix ? `${directUploadURL}?prefix=${prefix}` : directUploadURL;

      if (!this.file) {
        throw new Error('Upload halted: no file specified');
      }

      if (!endpoint) {
        throw new Error('Upload halted: no direct upload endpoint specified');
      }

      const { signedId } = await this.activeStorage.upload(this.file, endpoint, {
        onProgress: (progress) => {
          this.progress = progress;
        },
      });

      this.uploaded = true;

      return signedId;
    }),
  );
}
